import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import './JewelleryDetailPage.css';
import { useAuth } from '../context/AuthContext';

// Helper to get image URL
const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;
    const isProduction = process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost';
    const baseUrl = isProduction ? '' : 'http://localhost:5050';
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${baseUrl}${cleanPath.replace(/\\/g, '/')}`;
};

const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(price);
};

// Trust List Data
const trustPoints = [
    "Legacy of Manoj Abhushan Bhandar (Since 2002)",
    "BIS Hallmarked & Certified Purity",
    "Transparent pricing linked to Today's Gold Rate",
    "Trusted by generations across Rajasthan"
];

const JewelleryDetailPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [similarItems, setSimilarItems] = useState([]);
    const [allItems, setAllItems] = useState([]);

    // Slugify helper
    const createSlug = (i, itemsList) => {
        const basicSlug = i.name.toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');

        const duplicates = itemsList.filter(other => {
            const otherBasicSlug = other.name.toLowerCase()
                .trim()
                .replace(/[^\w\s-]/g, '')
                .replace(/[\s_-]+/g, '-')
                .replace(/^-+|-+$/g, '');
            return otherBasicSlug === basicSlug;
        });

        if (duplicates.length > 1 && i.weight) {
            return `${basicSlug}-${Math.floor(i.weight)}g`;
        }
        return basicSlug;
    };

    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            try {
                const res = await axios.get('/api/jewellery');
                const fetchedAllItems = res.data;
                setAllItems(fetchedAllItems);

                const foundItem = fetchedAllItems.find(i => {
                    const itemSlug = createSlug(i, fetchedAllItems);
                    return itemSlug === slug || i._id === slug;
                });

                if (foundItem) {
                    setItem(foundItem);
                    const related = fetchedAllItems.filter(i =>
                        i._id !== foundItem._id &&
                        (i.type === foundItem.type || i.category === foundItem.category)
                    ).slice(0, 4);
                    setSimilarItems(related);
                } else {
                    setError('Item not found');
                }
            } catch (err) {
                console.error(err);
                setError('Failed to load item details.');
            }
            setLoading(false);
        };
        fetchDetails();
        window.scrollTo(0, 0);
    }, [slug]);

    if (loading) return <div className="loading-container"><div className="loading-spinner"></div></div>;
    if (error) return <div className="error-container">{error} <Link to="/jewellery">Back to Collection</Link></div>;
    if (!item) return null;

    // --- Dynamic Content Generation ---
    const pageTitle = item.name;
    const isHeavy = item.weight > 20;
    const isWedding = item.name.toLowerCase().includes('set') || item.type === 'Gold' && isHeavy;
    const occasion = isWedding ? "Bridal | Festive | Heirloom" : "Festive | Daily Luxury";
    const subtitle = `Handcrafted ${item.purity || '22K'} Heritage ${item.type} from Rajasthan`;

    const imageUrl = getImageUrl(item.image);
    const canonicalUrl = `https://mabjewels.com/jewellery/${slug}`;

    const productSchema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": item.name,
        "description": item.description || subtitle,
        "image": imageUrl ? (imageUrl.startsWith('http') ? imageUrl : `https://mabjewels.com${imageUrl}`) : "https://mabjewels.com/logo.png",
        "brand": { "@type": "Brand", "name": "MAB Jewels" },
        "material": item.purity || "22K Gold",
        "weight": { "@type": "QuantitativeValue", "value": item.weight, "unitCode": "GRM" },
        "offers": {
            "@type": "Offer",
            "priceCurrency": "INR",
            "price": item.price,
            "availability": item.status === 'Sold' ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
            "seller": { "@type": "Organization", "name": "MAB Jewels" },
            "url": canonicalUrl
        },
        ...(item.huid && { "identifier": item.huid })
    };

    const waBase = `https://wa.me/919414817620?text=`;
    const waExpert = `${waBase}${encodeURIComponent(`Namaste, I would like to speak to an expert about *${item.name}* (Weight: ${item.weight}g, Price: ₹${item.price}).\n\nI am interested in this heritage piece.`)}`;
    const waVideoCall = `${waBase}${encodeURIComponent(`Namaste, I would like to request a *Video Call* to see *${item.name}* (Weight: ${item.weight}g) live.\n\nPlease let me know a convenient time. Thank you.`)}`;
    const waMeasurements = `${waBase}${encodeURIComponent(`Namaste, I am interested in *${item.name}*.\n\nHere are my measurements / customisation details:\n\n[Please add your size, finger size, wrist size, etc. here]\n\nKindly let me know if this can be customised. Thank you.`)}`;

    return (
        <div className="detail-page-container">
            <Helmet>
                <title>{item.name} – {item.purity || '22K'} {item.type} | MAB Jewels Jodhpur</title>
                <meta name="description" content={`Buy ${item.name} – handcrafted ${item.purity || '22K'} ${item.type} from MAB Jewels, Jodhpur. Weight: ${item.weight}g. BIS hallmarked & certified purity.`} />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
            </Helmet>
            <div className="detail-content-wrapper">

                {/* 1. EXPERIENCE: Image Zoom */}
                {/* 1. EXPERIENCE: Image Zoom (Main Interaction) */}
                <div className="detail-image-section">
                    <div
                        className="image-zoom-container"
                        onMouseMove={(e) => {
                            const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
                            const x = ((e.clientX - left) / width) * 100;
                            const y = ((e.clientY - top) / height) * 100;
                            e.currentTarget.style.setProperty('--x', `${x}%`);
                            e.currentTarget.style.setProperty('--y', `${y}%`);
                        }}
                    >
                        {item.image ? (
                            <>
                                <img src={getImageUrl(item.image)} alt={item.name} className="detail-main-image" />
                                <div className="zoom-hint">Hover to Zoom</div>
                            </>
                        ) : (
                            <div className="detail-placeholder">No Image Available</div>
                        )}
                    </div>
                </div>

                {/* 2. INFO SECTION */}
                <div className="detail-info-section">
                    <div className="detail-category">{item.type} Collection</div>
                    <h1 className="detail-title">{pageTitle}</h1>
                    <h2 className="detail-subtitle">{subtitle}</h2>

                    {/* Price with Transparency Note */}
                    <div className="detail-price-block">
                        <div className="detail-price">{formatPrice(item.price)}</div>
                        <div className="price-note">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                            Transparent pricing aligned with today's gold rate
                        </div>
                    </div>

                    {/* 3. ARTISAN STORY (The "Gold") */}
                    {item.karigar ? (
                        <div className="artisan-story-block">
                            <div className="artisan-heading">
                                <span>⚒</span> Artisan Heritage
                            </div>
                            <p className="artisan-text">
                                Crafted by {item.karigar}, a master goldsmith carrying forward the centuries-old Jadau traditions of Rajasthan.
                                Each curve is shaped by hand, preserving techniques passed down through generations to create this timeless heirloom.
                            </p>
                        </div>
                    ) : (
                        <div className="artisan-story-block">
                            <div className="artisan-heading">
                                <span>⚒</span> Rajasthani Handcraft
                            </div>
                            <p className="artisan-text">
                                This piece is a testament to the master artisanship of Jodhpur. Handcrafted using traditional techniques,
                                it carries the warmth of human touch and the legacy of royal lineage.
                            </p>
                        </div>
                    )}

                    {/* 4. SPECS: Quiet Confidence */}
                    <div className="detail-specs-section">
                        <div className="section-heading-small">Jewellery Details</div>
                        <div className="specs-grid">
                            <div className="spec-item">
                                <span className="spec-label">Gold Purity</span>
                                <span className="spec-value">{item.purity || '22K'} BIS Hallmarked</span>
                            </div>
                            <div className="spec-item">
                                <span className="spec-label">Net Weight</span>
                                <span className="spec-value">{item.weight} grams</span>
                            </div>
                            <div className="spec-item">
                                <span className="spec-label">Craft Technique</span>
                                <span className="spec-value">Handcrafted Rajasthani Jadau</span>
                            </div>
                            <div className="spec-item">
                                <span className="spec-label">Occasion</span>
                                <span className="spec-value">{occasion}</span>
                            </div>
                            {item.huid && (
                                <div className="spec-item">
                                    <span className="spec-label">HUID Verified</span>
                                    <span className="spec-value">{item.huid}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 5. TRUST BLOCK: Why MAB? */}
                    <div className="trust-block-mini">
                        <div className="section-heading-small" style={{ marginBottom: '15px' }}>Why MAB Jewels</div>
                        <ul className="trust-list">
                            {trustPoints.map((point, idx) => (
                                <li key={idx} className="trust-item">
                                    <span className="check-icon">✓</span> {point}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* 6. CTA: Expert Consultation */}
                    <div className="detail-actions">
                        <a href={waExpert} target="_blank" rel="noreferrer" className="btn-expert-chat">
                            Speak to Our Jewellery Expert
                        </a>

                        <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
                            <a
                                href={waVideoCall}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                    flex: 1, minWidth: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    gap: '8px', padding: '11px 16px', border: '2px solid #25D366', borderRadius: '8px',
                                    color: '#1a7d42', fontWeight: '600', fontSize: '0.9rem', textDecoration: 'none',
                                    background: '#f0fff4', textAlign: 'center'
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366"><path d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4z"/></svg>
                                Request a Video Call
                            </a>
                            <a
                                href={waMeasurements}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                    flex: 1, minWidth: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    gap: '8px', padding: '11px 16px', border: '2px solid #d4af37', borderRadius: '8px',
                                    color: '#8b6914', fontWeight: '600', fontSize: '0.9rem', textDecoration: 'none',
                                    background: '#fffbea', textAlign: 'center'
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                Send My Measurements
                            </a>
                        </div>

                        <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#999', marginTop: '12px' }}>
                            Available for customization & guidance
                        </p>
                    </div>

                </div>
            </div>

            {/* 7. COMPLETE THE LOOK */}
            {similarItems.length > 0 && (
                <div className="complete-look-section">
                    <div className="detail-content-wrapper" style={{ flexDirection: 'column', gap: '40px' }}>
                        <h2 className="section-title-center">Complete the Look</h2>
                        <div className="related-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: '30px',
                            width: '100%'
                        }}>
                            {similarItems.map(sim => {
                                const simSlug = createSlug(sim, allItems);
                                return (
                                    <div className="product-card" key={sim._id} onClick={() => { navigate(`/jewellery/${simSlug}`); window.scrollTo(0, 0); }}>
                                        <div className="product-image-container">
                                            {sim.image ? <img src={getImageUrl(sim.image)} alt={sim.name} className="product-img" loading="lazy" /> : null}
                                        </div>
                                        <div className="product-info">
                                            <h3 className="product-title" style={{ fontFamily: 'Playfair Display, serif' }}>{sim.name}</h3>
                                            <div className="product-price">{formatPrice(sim.price)}</div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JewelleryDetailPage;
